import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Chip, Button,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Event, PendingActions, Groups, HourglassBottom, ChevronRight, TrendingUp, TrendingDown, TaskAlt,
} from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";

const TINT = {
  primary: "primary",
  warning: "warning",
  secondary: "secondary",
  error: "error",
};

function Legend({ color, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function MetricCard({ icon, tint = "primary", label, value, sub, onClick }) {
  const role = TINT[tint] || "primary";
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        cursor: onClick ? "pointer" : "default",
        transition: "border-color .15s",
        "&:hover": onClick ? { borderColor: `${role}.main` } : {},
      }}
    >
      <Box
        sx={{
          width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: (t) => alpha(t.palette[role].main, 0.14),
          color: `${role}.main`,
          "& svg": { fontSize: 21 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
        <Typography variant="h5" fontWeight={700} lineHeight={1.1}>{value}</Typography>
      </Box>
      {sub && <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{sub}</Typography>}
      {onClick && <ChevronRight fontSize="small" sx={{ color: "text.disabled" }} />}
    </Paper>
  );
}

// 상대 시간 표기 (승인 대기 요청 경과 시간)
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return day === 1 ? "어제" : `${day}일 전`;
}

function EmptyRow({ icon, text }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={0.75} sx={{ py: 4, color: "text.disabled" }}>
      <Box sx={{ "& svg": { fontSize: 32, opacity: 0.5 } }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Stack>
  );
}

// 처리 대기 리스트 행 공통 스타일
const queueRowSx = (withBorder) => ({
  py: 0.85, px: 0.5, mx: -0.5, borderRadius: 1, cursor: "pointer",
  borderBottom: withBorder ? 1 : 0, borderColor: "divider",
  "&:hover": { bgcolor: "action.hover" },
});

export default function AdminDashboard({ onGotoSection }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/dashboard/member-trend?months=12").catch(() => ({ data: { points: [] } })),
    ])
      .then(([s, t]) => {
        if (!alive) return;
        setSummary(s.data);
        setTrend(t.data?.points || []);
      })
      .catch(() => { if (alive) setError("대시보드 데이터를 불러오지 못했습니다."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !summary) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        <Alert severity="error">{error || "데이터가 없습니다."}</Alert>
      </Box>
    );
  }

  const { byType, activeMembers, todayLessons, pendingRequests, waitlist, expiringTicketsCount, expiringDays, today } = summary;
  const todayList = summary.todayLessonsList || [];
  const pendingList = summary.pendingRequestsList || [];
  const expiringList = summary.expiringTickets || [];
  const provisionalCount = todayList.filter((r) => r.isProvisional).length;
  const todayLabel = today ? today.slice(5).replace("-", "/") : "";

  const xLabels = trend.map((p) => `${Number(String(p.month).split("-")[1])}월`);
  const yData = trend.map((p) => p.activeMembers);
  const hasChart = trend.length >= 2;
  const delta = hasChart ? trend[trend.length - 1].activeMembers - trend[0].activeMembers : 0;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="h6" fontWeight={700} mb={2}>대시보드</Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.55fr 1fr" }, gap: 2, alignItems: "stretch" }}>
        {/* 왼쪽: 활성 회원 추이 */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", flexDirection: "column" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" rowGap={1}>
            <Box>
              <Typography variant="body2" color="text.secondary">활성 회원</Typography>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="h3" fontWeight={700} lineHeight={1.1}>{activeMembers}</Typography>
                {hasChart && delta !== 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: delta > 0 ? "success.main" : "error.main" }}>
                    {delta > 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                    <Typography variant="body2" fontWeight={600}>{delta > 0 ? `+${delta}` : delta}</Typography>
                    <Typography variant="caption" color="text.secondary">· 최근 {trend.length}개월</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Legend color="primary.main" label={`횟수제 ${byType.COUNT}`} />
              <Legend color="success.main" label={`고정제 ${byType.FIXED}`} />
              <Legend color="secondary.main" label={`스케줄 ${byType.SCHEDULE}`} />
            </Stack>
          </Stack>

          {hasChart ? (
            <Box sx={{ mt: 1, width: "100%" }}>
              <LineChart
                height={210}
                // right: 마지막 눈금 라벨(예: "10월")이 점 중심에 걸쳐 절반이 바깥으로 나가므로 그만큼 여백 확보 (부족하면 "1..."로 잘림)
                margin={{ left: 8, right: 24, top: 16, bottom: 22 }}
                xAxis={[{ data: xLabels, scaleType: "point" }]}
                series={[{ data: yData, color: theme.palette.primary.main, showMark: true, curve: "monotoneX" }]}
                grid={{ horizontal: true }}
                hideLegend
              />
            </Box>
          ) : (
            <Box sx={{ height: 210, mt: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, color: "text.secondary" }}>
              <TrendingUp sx={{ fontSize: 40, opacity: 0.4 }} />
              <Typography variant="body2" textAlign="center">
                회원 추이를 표시할 데이터가 아직 부족합니다.
                <br />가입 이력이 2개월 이상 쌓이면 그래프가 나타납니다.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* 오른쪽: 지표 4종 */}
        <Stack spacing={1.25}>
          <MetricCard
            icon={<Event />} tint="primary" label="오늘 레슨" value={todayLessons}
            sub={provisionalCount > 0 ? `임시 ${provisionalCount}건` : null}
            onClick={() => navigate("/lesson")}
          />
          <MetricCard
            icon={<PendingActions />} tint="warning" label="승인 대기 요청" value={pendingRequests}
            onClick={() => navigate("/lesson-requests")}
          />
          <MetricCard
            icon={<Groups />} tint="secondary" label="빈자리 대기" value={waitlist ?? 0}
            onClick={() => navigate("/lesson-requests?tab=waitlist")}
          />
          <MetricCard
            icon={<HourglassBottom />} tint="error" label="만료 임박 티켓" value={expiringTicketsCount}
            sub={`${expiringDays}일 이내`}
            onClick={() => onGotoSection?.("members")}
          />
        </Stack>
      </Box>

      {/* 하단: 오늘의 운영 (요약 카드 뒤의 실제 처리 항목) */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1.25 }}>오늘의 운영</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2, alignItems: "stretch" }}>
        {/* 왼쪽: 오늘의 레슨 타임라인 */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", flexDirection: "column" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Event fontSize="small" sx={{ color: "primary.main" }} />
              <Typography variant="subtitle2" fontWeight={700}>오늘의 레슨</Typography>
            </Stack>
            <Button
              size="small" endIcon={<ChevronRight />} onClick={() => navigate("/lesson")}
              sx={{ color: "text.secondary", minWidth: 0 }}
            >
              {todayLabel} · {todayList.length}건
            </Button>
          </Stack>
          {todayList.length === 0 ? (
            <EmptyRow icon={<Event />} text="오늘 예정된 레슨이 없습니다." />
          ) : (
            <Box>
              {todayList.map((r, i) => (
                <Stack
                  key={r.id} direction="row" alignItems="center" spacing={1.5}
                  onClick={() => navigate("/lesson")}
                  sx={{
                    py: 0.9, px: 0.5, mx: -0.5, borderRadius: 1, cursor: "pointer",
                    borderBottom: i < todayList.length - 1 ? 1 : 0, borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ width: 46, flexShrink: 0 }}>
                    {r.startTime}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {r.memberName}
                    <Box component="span" sx={{ color: "text.disabled" }}> · {r.teacherName}</Box>
                  </Typography>
                  {r.isProvisional && (
                    <Chip label="임시" size="small" color="warning" variant="outlined" sx={{ height: 20, flexShrink: 0 }} />
                  )}
                </Stack>
              ))}
            </Box>
          )}
        </Paper>

        {/* 오른쪽: 처리 대기 큐 */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
            <PendingActions fontSize="small" sx={{ color: "warning.main" }} />
            <Typography variant="subtitle2" fontWeight={700}>처리 대기</Typography>
          </Stack>

          {pendingList.length === 0 && expiringList.length === 0 ? (
            <EmptyRow icon={<TaskAlt />} text="처리할 항목이 없습니다." />
          ) : (
            <Stack spacing={1.75}>
              {pendingList.length > 0 && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                    <Typography variant="caption" color="text.secondary">승인 대기 요청</Typography>
                    <Typography variant="caption" fontWeight={700} color="warning.main">{pendingList.length}</Typography>
                  </Stack>
                  {pendingList.slice(0, 5).map((r, i, arr) => (
                    <Stack
                      key={r.id} direction="row" alignItems="center" spacing={1}
                      onClick={() => navigate("/lesson-requests")}
                      sx={queueRowSx(i < arr.length - 1)}
                    >
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {r.memberName}
                        <Box component="span" sx={{ ml: 0.75, fontSize: 12, color: r.type === "CANCELLATION" ? "error.main" : "primary.main" }}>
                          {r.type === "CANCELLATION" ? "취소" : "예약"}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>{timeAgo(r.createdAt)}</Typography>
                    </Stack>
                  ))}
                  {pendingList.length > 5 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 0.75 }}>외 {pendingList.length - 5}건</Typography>
                  )}
                </Box>
              )}

              {expiringList.length > 0 && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                    <Typography variant="caption" color="text.secondary">만료 임박 티켓</Typography>
                    <Typography variant="caption" fontWeight={700} color="error.main">{expiringList.length}</Typography>
                  </Stack>
                  {expiringList.slice(0, 5).map((r, i, arr) => (
                    <Stack
                      key={r.batchId} direction="row" alignItems="center" spacing={1}
                      onClick={() => onGotoSection?.("members")}
                      sx={queueRowSx(i < arr.length - 1)}
                    >
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{r.memberName}</Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>잔여 {r.remaining}</Typography>
                      <Chip label={`D-${r.daysLeft}`} size="small" color="error" variant="outlined" sx={{ height: 20, flexShrink: 0 }} />
                    </Stack>
                  ))}
                  {expiringList.length > 5 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 0.75 }}>외 {expiringList.length - 5}건</Typography>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
